import React from "react";
import MUIBox from "@mui/material/Box";
import Layer from "../../Layer";
import CardQueueFiltersSidebar from "../../Component/Card/Queue/CardQueueFiltersSidebar";
import CardQueueDataGrid from "../../Component/Card/Queue/CardQueueDataGrid";
import Setting from "../../Utility/Setting";
import QueueService from "../../Seating/Security/QueueService/queueService";
import CardQueueDataGridOrderActivity from "../../Component/Card/Queue/CardQueueDataGrid/CardQueueDataGridOrderActivity";
import CardQueueDataGridOrderActivitySales from "../../Component/Card/Queue/CardQueueDataGrid/CardQueueDataGridOrderActivity/CardQueueDataGridOrderActivitySales";
import CardQueueDataGridPatientIssue from "../../Component/Card/Queue/CardQueueDataGrid/CardQueueDataGridPatientIssue";
import CardQueueDataGridPendingPreauth from "../../Component/Card/Queue/CardQueueDataGrid/CardQueueDataGridPendingPreauth";
import CardQueueDataGridBrightreeInvoice from "../../Component/Card/Queue/CardQueueDataGrid/CardQueueDataGridBrightreeInvoice";
import CardQueueDataGridServiceQuotePart from "../../Component/Card/Queue/CardQueueDataGrid/CardQueueDataGridServiceQuotePart";
import CardQueueDataGridNotification from "../../Component/Card/Queue/CardQueueDataGrid/CardQueueDataGridNotification";
import CardQueueDataGridInboundCommunication from "../../Component/Card/Queue/CardQueueDataGrid/CardQueueDataGridInboundCommunication";
import CardQueueDataGridBrightreeStopHold from "../../Component/Card/Queue/CardQueueDataGrid/CardQueueDataGridBrightreeStopHold";
import User from "../../Utility/Crud/User";
import Security from "../../Utility/Security";
import Load from "../Load";
import WidgetDashboardRow from "../../Component/WidgetDashboardRow";
import MUISkeleton from "@mui/material/Skeleton";
import Dashboard from "../../Utility/Crud/Dashboard";


export default class Queue extends Layer {
    constructor(props) {
        super(props);
        this.state.dashboardWidgets = props.dashboardWidgets;
        this.state.activeWidget = props.activeWidget;
        this.state.loadingWidgetConfigurationFromDashboard = true;
        this.state.sidebarCollapsed = Setting.get("queue.sidebarCollapsed") === true ? true : false;
        this.queueLayerVisibilityChangeHandler = this.handleVisibilityChange.bind(this);
    }

    /**
     * Set the data in the state.
     */
    afterComponentDidMount() {
        if (Dashboard.isDashboardRoute()) {
            return;
        }

        const queueId = this.props.match.params.id ? +this.props.match.params.id : null;

        if (queueId !== null) {
            this.setState({
                queue: this.context.queuesIndexed[queueId],
            });
            this.getQueueRows(queueId);
            this.getQueueStatistics(queueId);
        }

        document.addEventListener(
            "visibilitychange",
            this.queueLayerVisibilityChangeHandler
        );
    }

    componentDidUpdate(prevProps) {
        if (prevProps.widgetConfiguration !== this.props.widgetConfiguration) {
            this.setState({
                loadingWidgetConfigurationFromDashboard: false,
            })
            const queueId = Dashboard.isDashboardRoute() ? this.props.widgetConfiguration.dashboardConfiguration?.queueId : 1;
            if (queueId !== null) {
                this.setState({
                    queue: this.context.queuesIndexed[queueId],
                });
                this.getQueueRows(queueId);
                this.getQueueStatistics(queueId);
            }
        }
    }

    /**
     * Remove the visibilitychange event handler.
     */
    componentWillUnmount() {
        document.removeEventListener(
            "visibilitychange",
            this.queueLayerVisibilityChangeHandler
        );
        // super.componentWillUnmount(); // Layer no longer defines componentWillUnmount() so we can remove this
    }

    /**
     * Get prefilters based on the settings.
     *
     * @param {number} id The ID to get prefilters for.
     * @returns {array}
     */
    getPrefilters() {
        const prefilters = [];

        if (
            Security.hasPermission("queue.useRegionPrefilter") === false &&
            Security.hasPermission("queue.useLocationPrefilter") === false &&
            Security.hasPermission("queue.useUserPrefilter") === false &&
            Security.hasPermission("queue.useMarketingCampaignPrefilter") === false &&
            Security.hasPermission("queue.usePrimaryProductTypePrefilter") === false &&
            Security.hasPermission("queue.usePrimaryInsuranceTypePrefilter") === false
        ) {
            return prefilters;
        }

        /**
         * For all of these prefilters, we default to using the user's chosen
         * setting (if one is set). If the user did not explicitly pick one,
         * then it defaults to all that they have access to. However, if the
         * user has access to all, then just don't set a filter at all to avoid
         * sending hundreds of IDs in a filter.
         */

        // Region
        const filteredRegions = User.getFilteredRegions(
            this.context.currentUser,
            this.context.userprofileLocationsIndexed,
            this.context.regionsIndexed,
            this.context.locationsIndexed
        ).filter((region) => {
            if (Security.hasPermission("user.viewAllRegions") === true) {
                return true;
            } else {
                // Filter only regions with a type available to my prefilters
                return Object.values(this.context.teamUsersIndexed).some((teamUser) => {
                    if (teamUser.userId === this.context.currentUser.id) {
                        const team = this.context.teamsIndexed[teamUser.teamId];
                        if (team && team.departmentId) {
                            const department = this.context.departmentsIndexed[team.departmentId];
                            return (
                                department.jsonPrefilters?.regionTypeTermId === region.regionTypeTermId
                            );
                        }
                    }

                    return false;
                });
            }
        });
        const filteredRegionIds = filteredRegions.map(region => region.id);

        let prefilterRegionIds = null;
        if (Setting.get("queue.prefilters.regionIds")) {
            prefilterRegionIds = Setting.get("queue.prefilters.regionIds").filter(regionId =>
                filteredRegionIds.includes(regionId)
            );
            if (prefilterRegionIds.length === 0) {
                prefilterRegionIds = filteredRegionIds;
            }
        } else {
            if (!Security.hasPermission("user.viewAllRegions")) {
                prefilterRegionIds = filteredRegionIds;
            }
        }

        // Location
        const filteredLocations = User.getFilteredLocations(
            this.context.currentUser,
            this.context.userprofileLocationsIndexed,
            this.context.locationsIndexed
        );
        const filteredLocationIds = filteredLocations.map(location => location.id);

        let prefilterLocationIds = null;
        if (Setting.get("queue.prefilters.locationIds")) {
            prefilterLocationIds = Setting.get("queue.prefilters.locationIds").filter(locationId =>
                filteredLocationIds.includes(locationId)
            );
            if (prefilterLocationIds.length === 0) {
                prefilterLocationIds = filteredLocationIds;
            }
        } else {
            if (!Security.hasPermission("user.viewAllLocations")) {
                prefilterLocationIds = filteredLocationIds;
            }
        }

        // User
        const filteredUsers = User.getFilteredUsers(
            this.context.currentUser,
            this.context.userprofileLocationsIndexed,
            this.context.locationsIndexed,
            this.context.usersIndexed,
            this.context.salesPcrLinksIndexed
        );
        const filteredUserIds = filteredUsers.map(user => user.id);

        let prefilterUserIds = null;
        if (Setting.get("queue.prefilters.userIds")) {
            prefilterUserIds = Setting.get("queue.prefilters.userIds").filter(userId =>
                filteredUserIds.includes(userId)
            );
            if (prefilterUserIds.length === 0) {
                prefilterUserIds = filteredUserIds;
            }
        } else {
            if (!Security.hasPermission("user.viewAllUsers")) {
                prefilterUserIds = filteredUserIds;
            }
        }

        // Marketing Campaign
        const filteredMarketingCampaignIds = Object.values(this.context.marketingCampaignsIndexed)
            .map(marketingCampaign => marketingCampaign.id);

        let prefilterMarketingCampaignIds = null;
        if (
            Setting.get("queue.prefilters.marketingCampaignIds") &&
            Setting.get("queue.prefilters.marketingCampaignIds").length > 0
        ) {
            prefilterMarketingCampaignIds = Setting.get("queue.prefilters.marketingCampaignIds").filter(marketingCampaignId =>
                filteredMarketingCampaignIds.includes(marketingCampaignId)
            );
            if (prefilterMarketingCampaignIds.length === 0) {
                prefilterMarketingCampaignIds = filteredMarketingCampaignIds;
            }
        }

        // Set up the prefilter object.
        if (
            prefilterRegionIds &&
            Security.hasPermission("queue.useRegionPrefilter")
        ) {
            let key;
            if (Setting.get("queue.prefilters.regionTypeTermId") === 11) { // Sales
                key = "order_activity.sales_location.region_id"
            } else if (Setting.get("queue.prefilters.regionTypeTermId") === 12) { // Field Operations
                key = "order_activity.service_location.field_operations_region_id"
            } else {
                throw new Error(`Invalid queue.prefilters.regionTypeTermId: ${Setting.get("queue.prefilters.regionTypeTermId")}`);
            }

            prefilters.push({
                Key: key,
                Value: prefilterRegionIds.map(value => String(value)),
            });
        }

        if (
            prefilterLocationIds &&
            Security.hasPermission("queue.useLocationPrefilter")
        ) {
            let key;
            if (Setting.get("queue.prefilters.regionTypeTermId") === 11) { // Sales
                key = "order_activity.sales_location_id"
            } else if (Setting.get("queue.prefilters.regionTypeTermId") === 12) { // Field Operations
                key = "order_activity.service_location_id"
            } else {
                throw new Error(`Invalid queue.prefilters.regionTypeTermId: ${Setting.get("queue.prefilters.regionTypeTermId")}`);
            }

            prefilters.push({
                Key: key,
                Value: prefilterLocationIds.map(value => String(value)),
            });
        }

        if (
            prefilterUserIds &&
            Security.hasPermission("queue.useUserPrefilter")
        ) {
            prefilters.push({
                Key: "order_activity.activity.owner_id",
                // Key: (this.context.currentUser.isAtp) ? "order_activity.atp_id" : "order_activity.activity.owner_id",
                Value: prefilterUserIds.map(value => String(value)),
            });
        }

        if (
            prefilterMarketingCampaignIds &&
            Security.hasPermission("queue.useMarketingCampaignPrefilter")
        ) {
            prefilters.push({
                Key: "order_activity.marketing_campaign_id",
                Value: prefilterMarketingCampaignIds.map(value => String(value)),
            });
        }

        const filteredPrimaryProductTypeIds = Object.values(this.context.productTypesIndexed)
            .map((productType) => productType.id);
        const prefilterPrimaryProductTypeIds = (Setting.get("queue.prefilters.primaryProductTypeIds") || [])
            .filter((productTypeId) => filteredPrimaryProductTypeIds.includes(productTypeId));
        if (
            prefilterPrimaryProductTypeIds.length > 0 &&
            Security.hasPermission("queue.usePrimaryProductTypePrefilter")
        ) {
            prefilters.push({
                Key: "order_activity.order_activity_products._primary_product_type_id",
                Value: prefilterPrimaryProductTypeIds.map(value => String(value)),
            });
        }

        const filteredPrimaryInsuranceTypeIds = Object.values(this.context.insuranceTypesIndexed)
            .map((insuranceType) => insuranceType.id);
        const prefilterPrimaryInsuranceTypeIds = (Setting.get("queue.prefilters.primaryInsuranceTypeIds") || [])
            .filter((insuranceTypeId) => filteredPrimaryInsuranceTypeIds.includes(insuranceTypeId));
        if (
            prefilterPrimaryInsuranceTypeIds.length > 0 &&
            Security.hasPermission("queue.usePrimaryInsuranceTypePrefilter")
        ) {
            prefilters.push({
                Key: "order_activity.insurance_type_id",
                Value: prefilterPrimaryInsuranceTypeIds.map(value => String(value)),
            });
        }

        return prefilters;
    }

    /**
     * API call for getting queue data by id.
     */
    async getQueueRows(id) {
        const args = {
            id: id,
            prefilters: this.getPrefilters(),
        };

        this.setState({
            loadedAt: null
        });

        try {
            const promises = [];

            if (this.context.queuesIndexed[id] && this.context.queuesIndexed[id].table === "service_quote_part") {
                promises.push(
                    Load.getTableLazy("servicePartCatalog", this.context)
                )
            }

            promises.push(
                QueueService.getQueueRows(args)
                    .then((rows) => {
                        this.setState({
                            rows: rows,
                            query: Setting.get(`queue.${id}.searchQuery`) ? Setting.get(`queue.${id}.searchQuery`) : "",
                        });
                    })
            );
            await Promise.all(promises)
                .then(() => {
                    this.setState({
                        loadedAt: new Date(),
                        queueDirty: false,
                    });
                })
        } catch (error) {
            this.setState({
                loadFailed: true,
            });
            console.error(error);
        }
    }

    /**
     * API call for getting queue data history by id, start date, and end date.
     */
    async getQueueStatistics(id) {
        this.setState({ queueStatsLoading: true })
        var startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        const args = {
            startDate: startDate.toISOString().substring(0, 10),
            prefilters: this.getPrefilters(),
        };

        if (args.prefilters && args.prefilters.length > 0) {
            args.prefilters = JSON.stringify(args.prefilters);
        }

        return QueueService.getQueueStatistics(id, args)
            .then((stats) => {
                this.setState({
                    queueStatsLoading: false,
                    queueStatistics: stats,
                });
            })
            .catch((err) => {
                this.setState({
                    queueStatsLoading: false,
                });
                console.log(err);
            });
    }

    /**
     * Reload the queue when the tab becomes visible again if it's dirty.
     */
    handleVisibilityChange() {
        if (
            document.hidden === false &&
            this.state.queueDirty === true
        ) {
            this.getQueueRows(this.state.queue.id);
        }
    };

    /**
     * Renders page content.
     */
    renderContent() {
        let CardQueueDataGridComponent;
        if (Dashboard.isDashboardRoute() && this.state.loadingWidgetConfigurationFromDashboard) {
            CardQueueDataGridComponent = CardQueueDataGrid;  // Wait for widget configuration to load before determining the queue component
        } else {
            switch (this.state.queue.table) {
                case "order_activity":
                    CardQueueDataGridComponent = CardQueueDataGridOrderActivity;
                    break;
                case "order_activity.sales":
                    CardQueueDataGridComponent = CardQueueDataGridOrderActivitySales;
                    break;
                case "patient_issue":
                    CardQueueDataGridComponent = CardQueueDataGridPatientIssue;
                    break;
                case "pending_preauth":
                    CardQueueDataGridComponent = CardQueueDataGridPendingPreauth;
                    break;
                case "brightree_invoice":
                    CardQueueDataGridComponent = CardQueueDataGridBrightreeInvoice;
                    break;
                case "service_quote_part":
                    CardQueueDataGridComponent = CardQueueDataGridServiceQuotePart;
                    break;
                case "notification":
                    CardQueueDataGridComponent = CardQueueDataGridNotification;
                    break;
                case "inbound_communication":
                    CardQueueDataGridComponent = CardQueueDataGridInboundCommunication;
                    break;
                case "brightree_stop_hold":
                    CardQueueDataGridComponent = CardQueueDataGridBrightreeStopHold;
                    break;
                default:
                    CardQueueDataGridComponent = CardQueueDataGrid;
                    break;
            }
        }

        return (
            <>
                <MUIBox
                    sx={{
                        height: "calc(100vh - 82px)",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        flex: "1 1 auto",
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    {Dashboard.isDashboardRoute() && (
                        <MUIBox sx={{ flexShrink: 0 }}>
                            <WidgetDashboardRow
                                activeWidget={this.props.activeWidget ?? this.state.activeWidget}
                                dashboardWidgets={this.props.dashboardWidgets ?? this.state.dashboardWidgets}
                                onActiveWidgetChange={this.props.onActiveWidgetChange}
                                updateActiveWidgetConfiguration={this.props.updateActiveWidgetConfiguration}
                            />
                        </MUIBox>
                    )}
                    {Dashboard.isDashboardRoute() && this.state.loadingWidgetConfigurationFromDashboard === true ? (
                        <MUISkeleton variant="rectangular" width="100%" sx={{ flexGrow: 1, minHeight: 0 }} />
                    ) : (
                            <MUIBox sx={{ display: "flex", flexGrow: 1, minHeight: 0, alignItems: "stretch", gap: 2 }}>
                            <MUIBox
                                sx={{
                                    width: this.state.sidebarCollapsed ? "60px" : "260px",
                                    minHeight: 0,
                                    alignSelf: "stretch",
                                    display: "flex",
                                    flexDirection: "column",
                                    flexShrink: 0,
                                }}
                            >
                                <CardQueueFiltersSidebar
                                    rows={this.state.rows}
                                    queue={this.state.queue}
                                    loadedAt={this.state.loadedAt}
                                    collapsed={this.state.sidebarCollapsed}
                                    onCollapse={(collapsed) => {
                                        Setting.set("queue.sidebarCollapsed", collapsed);
                                        this.setState({
                                            sidebarCollapsed: collapsed
                                        });
                                    }}
                                    onPrefilterChange={() => {
                                        this.getQueueStatistics(this.state.queue.id);
                                        this.getQueueRows(this.state.queue.id);
                                    }}
                                    viewOutOfBoundsOnly={
                                        Dashboard.isDashboardRoute()
                                            ? (this.props.widgetConfiguration?.dashboardConfiguration?.viewOutOfBoundsOnly ? this.props.widgetConfiguration?.dashboardConfiguration?.viewOutOfBoundsOnly : null)
                                            : ((this.props.history.location.state && this.props.history.location.state.viewOutOfBoundsOnly) ? this.props.history.location.state.viewOutOfBoundsOnly : null)
                                    }
                                    queueStatistics={!this.state.queueStatsLoading ? this.state.queueStatistics : null}
                                />
                            </MUIBox>

                            {/* The goofy margin/padding here is necessary because the overflow breaks the box shadow on the card inside the box. */}
                            <MUIBox sx={{ flexGrow: 1, minHeight: 0, overflow: "hidden", overflowX: "hidden", margin: "-8px", padding: "8px", display: "flex", flexDirection: "column" }}>
                                <CardQueueDataGridComponent
                                    rows={this.state.rows}
                                    queue={this.state.queue}
                                    loadedAt={this.state.loadedAt}
                                    loadFailed={this.state.loadFailed}
                                    onReload={() => this.getQueueRows(this.state.queue.id)}
                                    onClickRow={() => this.setState({ queueDirty: true })}
                                    viewOutOfBoundsOnly={
                                        Dashboard.isDashboardRoute()
                                            ? (this.props.widgetConfiguration?.dashboardConfiguration?.viewOutOfBoundsOnly ? this.props.widgetConfiguration?.dashboardConfiguration?.viewOutOfBoundsOnly : null)
                                            : ((this.props.history.location.state && this.props.history.location.state.viewOutOfBoundsOnly) ? this.props.history.location.state.viewOutOfBoundsOnly : null)
                                    }
                                    widgetConfigurationViewOutOfBoundsOnly={Dashboard.isDashboardRoute() ? this.props.widgetConfiguration?.dashboardConfiguration?.viewOutOfBoundsOnly : null}
                                    history={this.props.history}
                                    query={this.state.query}
                                />
                            </MUIBox>
                        </MUIBox>
                    )}
                </MUIBox>
            </>
        );
    }
}
